import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../config/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { Plus, FileText, Clock, CheckCircle, XCircle, AlertTriangle, Calendar, Home, Eye, X, FileCheck, MessageSquare } from 'lucide-react';
import { checkUserHasAssociationApartments } from '../../api/housing';
import NoAssociationModal from '../../components/NoAssociationModal';
import OwnerDecisionModal from '../../components/OwnerDecisionModal';
import ExtendRentalModal from '../../components/ExtendRentalModal';
import ChangeTenantModal from '../../components/ChangeTenantModal';
import TerminateRentalModal from '../../components/TerminateRentalModal';
import ChatPanel from '../../components/chat/ChatPanel';
import AttachmentsSection from '../../components/AttachmentsSection';
import ChatToggleButton from '../../components/chat/ChatToggleButton';
import { Dialog } from '@headlessui/react';
import { useAPI } from '../../hooks/useAPI';
import { useToast } from '../../components/ui/ToastProvider';
import { rentalPhaseLabel } from '../../utils/applicationStats';

const PHASE_BADGE_CLASS = {
  green: 'bg-green-100 text-green-800 border-green-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function MyApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNoAssociationModal, setShowNoAssociationModal] = useState(false);
  const [noAssociationPendingClaim, setNoAssociationPendingClaim] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState('details');
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [showOwnerDecision, setShowOwnerDecision] = useState(false);
  const [decisionApp, setDecisionApp] = useState(null);
  const navigate = useNavigate();

  // State för rental management modaler
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showChangeTenantModal, setShowChangeTenantModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [activeRental, setActiveRental] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');

  // API hook för rental management
  const { extendRental, initiateTermination } = useAPI();
  const toast = useToast();

  // Funktion för att hantera "Skapa ansökan" klick
  const handleCreateApplication = async () => {
    if (!auth.currentUser?.uid) {
      navigate('/signin');
      return;
    }

    try {
      const associationCheck = await checkUserHasAssociationApartments(auth.currentUser.uid);
      
      if (!associationCheck.hasAssociationApartments) {
        setNoAssociationPendingClaim(Boolean(associationCheck.hasPendingClaim));
        setShowNoAssociationModal(true);
        return;
      }

      navigate('/create');
    } catch (error) {
      // Om kontrollen misslyckas, låt användaren gå till create-sidan ändå
      navigate('/create');
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        fetchUserApplications(user.uid);
        // Hämta användarens namn för chatten
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setCurrentUserName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
          }
        } catch (e) {}
      } else {
        setError('Ingen användare inloggad');
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchUserApplications = async (uid) => {
    setLoading(true);
    setError(null);
    try {
      // Hämta användarens ansökningar med rätt Firestore-fråga
      const applicationsRef = collection(db, 'applications');
      
      // Skapa två frågor: en för createdBy och en för ownedBy
      const createdByQuery = query(applicationsRef, where('createdBy', '==', uid));
      const ownedByQuery = query(applicationsRef, where('ownedBy', '==', uid));
      
      // Kör båda frågorna parallellt
      const [createdBySnapshot, ownedBySnapshot] = await Promise.all([
        getDocs(createdByQuery),
        getDocs(ownedByQuery)
      ]);
      
      // Kombinera resultaten och ta bort dubbletter
      const allApps = [];
      const processedIds = new Set();
      
      // Lägg till ansökningar där användaren är skapare
      createdBySnapshot.docs.forEach(doc => {
        if (!processedIds.has(doc.id)) {
          allApps.push({
            id: doc.id,
            ...doc.data()
          });
          processedIds.add(doc.id);
        }
      });
      
      // Lägg till ansökningar där användaren är ägare
      ownedBySnapshot.docs.forEach(doc => {
        if (!processedIds.has(doc.id)) {
          allApps.push({
            id: doc.id,
            ...doc.data()
          });
          processedIds.add(doc.id);
        }
      });
      
      // Hämta ägarnamn för ansökningar som saknar det
      const enrichedApps = await Promise.all(allApps.map(async (app) => {
        // Om ansökan redan har ägarnamn, returnera den som den är
        if (app.ownerFirstName && app.ownerLastName) {
          return app;
        }

        // Annars, hämta ägarnamn från users-kollektionen
        try {
          const userDoc = await getDoc(doc(db, 'users', app.createdBy));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
              ...app,
              ownerFirstName: userData.firstName || '',
              ownerLastName: userData.lastName || '',
              ownerEmail: userData.email || '',
              ownerPhoneNumber: userData.phoneNumber || ''
            };
          }
        } catch (error) {
        }
        return app;
      }));

      // Sortera efter senast skapade först
      const sortedApps = enrichedApps.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.fromDate || 0);
        const dateB = new Date(b.createdAt || b.fromDate || 0);
        return dateB - dateA; // Senaste först
      });

      setApplications(sortedApps);
    } catch (err) {
      setError('Kunde inte hämta ansökningar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download
  const handlePdfDownload = async (pdfUrl) => {
    setPdfDownloadLoading(true);
    try {
      if (!pdfUrl) {
        return;
      }

      if (pdfUrl.startsWith('gs://')) {
        const path = pdfUrl.replace('gs://hyria-storage/', '');
        const storageRef = ref(storage, path);
        const downloadUrl = await getDownloadURL(storageRef);
        window.open(downloadUrl, '_blank');
      } else if (pdfUrl.startsWith('https://')) {
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
    } finally {
      setPdfDownloadLoading(false);
    }
  };

  // Check if application needs owner decision
  const needsOwnerDecision = (app) => {
    // Kolla om BRF har godkänt och ägare inte har tagit beslut ännu
    return app.status === 'AWAITING_OWNER_DECISION';
  };

  // Handle owner decision
  const handleOwnerDecision = (decision) => {
    // Uppdatera lokal state efter beslut
    fetchUserApplications(auth.currentUser.uid);
    setShowOwnerDecision(false);
    setDecisionApp(null);
  };

  // Open owner decision modal
  const openOwnerDecisionModal = (app) => {
    setDecisionApp(app);
    setShowOwnerDecision(true);
  };

  // Hantera förlängning av uthyrning
  const handleExtendRental = async (newEndDate) => {
    if (!activeRental) return;

    try {
      await extendRental(activeRental.id, newEndDate);
      // Uppdatera listan
      fetchUserApplications(auth.currentUser.uid);
      setShowExtendModal(false);
      setActiveRental(null);
    } catch (error) {
      console.error('Error extending rental:', error);
      throw error;
    }
  };

  // Hantera byte av hyresgäst. Perioden följer med i adressen: sidan läser
  // den gamla ansökan från databasen och kontrollerar perioden mot den
  // godkända uthyrningen. Inget i sessionStorage, ingen guide.
  const handleChangeTenant = async (newStartDate, newEndDate) => {
    if (!activeRental) return;
    const applicationId = activeRental.id;
    setShowChangeTenantModal(false);
    setActiveRental(null);
    navigate(`/applications/${applicationId}/change-tenant?start=${newStartDate}&end=${newEndDate}`);
  };

  // Hantera uppsägning av uthyrning
  const handleTerminateRental = async (moveOutDate, reason) => {
    if (!activeRental) return;

    try {
      await initiateTermination(activeRental.id, moveOutDate, reason);

      // Tydlig bekräftelse till bostadsägaren (utöver modalens success-vy)
      toast({
        type: 'success',
        message: 'Uppsägning initierad – hyresgästen får ett mejl med signeringslänk.',
      });

      // Uppdatera listan. Ett fel här (efter lyckad uppsägning) får INTE
      // bubbla upp och kasta modalen i dess felgren – uppsägningen är redan klar.
      try {
        await fetchUserApplications(auth.currentUser.uid);
      } catch (refreshError) {
        console.error('Kunde inte uppdatera listan efter uppsägning:', refreshError);
      }
    } catch (error) {
      console.error('Error initiating termination:', error);
      throw error;
    }
  };

  // Beräkna statistik
  // Arkiverade ansökningar (medlem borttagen) räknas inte, men visas i listan med märkning.
  const liveApplications = applications.filter(app => !app.archivedAt);
  const stats = {
    total: liveApplications.length,
    pending: liveApplications.filter(app => app.status === 'PENDING').length,
    approved: liveApplications.filter(app => app.status === 'APPROVED').length,
    active: liveApplications.filter(app => app.status === 'ACTIVE' || app.status === 'FINAL_APPROVED').length,
    rejected: liveApplications.filter(app => app.status === 'REJECTED' || app.status === 'FINAL_REJECTED').length,
    creditCheck: liveApplications.filter(app => app.status === 'CREDIT_CHECK_COMPLETE').length,
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'TENANT_INFO_SUBMITTED': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'APPROVED': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'ACTIVE':
      case 'FINAL_APPROVED': return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'CREDIT_CHECK_COMPLETE': return <FileCheck className="w-5 h-5" style={{ color: '#62B8E9' }} />;
      case 'REJECTED':
      case 'FINAL_REJECTED': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Väntar på svar';
      case 'TENANT_INFO_SUBMITTED': return 'Väntar på kreditkontroll';
      case 'APPROVED': return 'Godkänd';
      case 'ACTIVE': return 'Aktiv';
      case 'FINAL_APPROVED': return 'Aktiv uthyrning';
      case 'AWAITING_OWNER_DECISION': return null; // Ingen badge, endast knapp visas
      case 'OWNER_REJECTED': return 'Avböjd av ägare';
      case 'CREDIT_CHECK_COMPLETE': return 'Kreditkontroll klar';
      case 'REJECTED':
      case 'FINAL_REJECTED': return 'Avböjd';
      default: return status;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'TENANT_INFO_SUBMITTED': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'APPROVED': return 'bg-green-100 text-green-800 border-green-200';
      case 'ACTIVE': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'FINAL_APPROVED': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'OWNER_REJECTED': return 'bg-red-100 text-red-800 border-red-200';
      case 'CREDIT_CHECK_COMPLETE': return 'bg-blue-50 text-[#62B8E9] border-blue-200';
      case 'REJECTED':
      case 'FINAL_REJECTED': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ej angivet';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  // Fält som skrivs med serverTimestamp kommer tillbaka som Firestore-Timestamp,
  // inte som sträng — new Date() på ett sådant ger "Invalid Date".
  const formatMoment = (value) => {
    if (!value) return null;
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('sv-SE');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Ett fel uppstod</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Simple Header */}
        <div className="mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-zinc-800 mb-2">Mina ansökningar</h1>
            <p className="text-zinc-600">
              {applications.length === 0 
                ? 'Du har inga ansökningar än' 
                : `${applications.length} ansökning${applications.length !== 1 ? 'ar' : ''} för andrahandsuthyrning`
              }
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        {applications.length > 0 && (
          <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
            <span className="font-medium">{stats.total} ansökning{stats.total !== 1 ? 'ar' : ''}</span>
            <span>•</span>
            <span className="text-green-600 font-medium">{stats.approved + stats.active} godkänd{stats.approved + stats.active !== 1 ? 'a' : ''}</span>
            {stats.pending > 0 && (
              <>
                <span>•</span>
                <span className="text-yellow-600 font-medium">{stats.pending} väntar</span>
              </>
            )}
            {stats.rejected > 0 && (
              <>
                <span>•</span>
                <span className="text-red-600 font-medium">{stats.rejected} avböjd{stats.rejected !== 1 ? 'a' : ''}</span>
              </>
            )}
          </div>
        )}

        {/* Applications List */}
        <div className="bg-white rounded-xl shadow">
          {applications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Kom igång med din första ansökan</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Skapa din första ansökan för andrahandsuthyrning och börja hyra ut din lägenhet.
              </p>
              <button
                onClick={handleCreateApplication}
                className="text-white px-8 py-3 rounded-xl transition-colors font-semibold text-lg"
                style={{ backgroundColor: '#62B8E9' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9FD1'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Skapa din första ansökan
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {applications.map((app) => {
                const hasClickableContent = app.status === 'CREDIT_CHECK_COMPLETE' || app.status === 'FINAL_APPROVED';
                const chatUnread = app.chatUnread?.[auth.currentUser?.uid] || 0;

                return (
                  <div
                    key={app.id}
                    className="p-6 transition-colors hover:bg-blue-50 cursor-pointer"
                    onClick={() => {
                      if (needsOwnerDecision(app)) {
                        openOwnerDecisionModal(app);
                      } else {
                        setSelectedApp(app);
                        setModalTab('details');
                        setModalOpen(true);
                      }
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            hasClickableContent ? 'bg-blue-50' : 'bg-blue-100'
                          }`}>
                            {hasClickableContent ? (
                              <FileCheck className="w-6 h-6" style={{ color: '#62B8E9' }} />
                            ) : (
                              <Home className="w-6 h-6 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">
                                {app.address || 'Adress ej angiven'}
                              </h3>
                              {app.status === 'CREDIT_CHECK_COMPLETE' && (
                                <span className="px-2 py-0.5 text-white text-xs font-bold rounded-full animate-pulse" style={{ backgroundColor: '#62B8E9' }}>
                                  NY
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              Lägenhet {app.apartmentNumber} • {app.city || ''}
                              <span className="text-gray-700"> • Ägare: {
                                (app.ownerFirstName && app.ownerLastName)
                                  ? `${app.ownerFirstName} ${app.ownerLastName}`
                                  : 'Okänd ägare'
                              }</span>
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(app.fromDate)} - {formatDate(app.toDate)}
                              </div>
                              {app.tenantFirstName && (
                                <div className="hidden sm:block">
                                  Hyresgäst: {app.tenantFirstName} {app.tenantLastName}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {app.archivedAt && (
                          <div className="px-3 py-1 rounded-full border text-sm font-medium bg-gray-100 text-gray-600 border-gray-200">
                            Arkiverad
                          </div>
                        )}
                        {(() => {
                          // En godkänd uthyrning som inte börjat säger när den startar —
                          // inte "Aktiv uthyrning". Fasen kommer ur datumen.
                          const phase = rentalPhaseLabel(app);
                          const text = phase ? phase.text : getStatusText(app.status);
                          const cls = phase ? PHASE_BADGE_CLASS[phase.tone] : getStatusBadgeClass(app.status);
                          return text ? (
                            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${cls}`}>
                              {getStatusIcon(app.status)}
                              {text}
                            </div>
                          ) : null;
                        })()}

                        {/* Action Button for APPROVED applications */}
                        {app.status === 'APPROVED' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/create?step=2&id=${app.id}`);
                            }}
                            className="text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                            style={{ backgroundColor: '#62B8E9' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9FD1'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                          >
                            Lägg till hyresgäst
                          </button>
                        )}

                        {/* Decision Button for applications where tenant is approved */}
                        {needsOwnerDecision(app) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openOwnerDecisionModal(app);
                            }}
                            className="text-white px-6 py-2.5 rounded-lg transition-colors text-sm font-semibold shadow-lg"
                            style={{ backgroundColor: '#62B8E9' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9FD1'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                          >
                            📋 Granska & Ta beslut
                          </button>
                        )}

                        {chatUnread > 0 && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-[#62B8E9]/10 rounded-full">
                            <MessageSquare className="w-4 h-4 text-[#62B8E9]" />
                            <span className="text-xs font-bold text-[#62B8E9]">{chatUnread}</span>
                          </div>
                        )}

                        <div className="text-xs text-gray-400">
                          {formatDate(app.createdAt)}
                        </div>
                      </div>
                    </div>

                    {/* Show rejection reason if application was rejected */}
                    {(app.status === 'REJECTED' || app.status === 'FINAL_REJECTED') && (app.rejectionReason || app.finalRejectionReason) && (
                      <div className="mt-3 ml-16 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm font-semibold text-red-800 mb-1">Orsak till avslag:</p>
                        <p className="text-sm text-red-700">{app.finalRejectionReason || app.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Credit Check Modal */}
      <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setModalTab('details'); }} className="fixed z-[1300] inset-0 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-2xl mx-auto max-h-[90vh] flex flex-col">
          <button
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
            onClick={() => { setModalOpen(false); setModalTab('details'); }}
            aria-label="Stäng"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedApp && (
            <>
            {/* Header */}
            <div className="p-6 pb-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
                  {selectedApp.status === 'FINAL_APPROVED' && (
                    <span className="inline-block mr-2 text-green-500">✓</span>
                  )}
                  {selectedApp.address || 'Adress ej angiven'}
                </h2>
                {selectedApp.status === 'FINAL_APPROVED' && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                    Godkänd uthyrning
                  </span>
                )}
                {selectedApp.status === 'CREDIT_CHECK_COMPLETE' && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-50 border border-blue-200" style={{ color: '#62B8E9' }}>
                    Kreditkontroll klar
                  </span>
                )}
              </div>

              {/* Tab bar */}
              <div className="flex border-b border-[#E2EAF0] mt-4">
                <button
                  onClick={() => setModalTab('details')}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    modalTab === 'details'
                      ? 'border-[#62B8E9] text-[#62B8E9]'
                      : 'border-transparent text-[#626262] hover:text-[#62B8E9]'
                  }`}
                >
                  Detaljer
                </button>
                <ChatToggleButton
                  active={modalTab === 'chat'}
                  unreadCount={selectedApp.chatUnread?.[auth.currentUser?.uid] || 0}
                  onClick={() => setModalTab('chat')}
                />
              </div>
            </div>

            {/* Tab content */}
            {modalTab === 'chat' ? (
              <ChatPanel
                applicationId={selectedApp.id}
                currentUser={{
                  uid: auth.currentUser?.uid || '',
                  name: currentUserName || 'Ägare',
                  role: 'owner',
                }}
              />
            ) : (
            <div className="overflow-y-auto p-6 space-y-4">
              {/* Info */}
              <div className="space-y-2">
                <p className="text-sm text-[#626262]">
                  📍 Lägenhet {selectedApp.apartmentNumber} • {selectedApp.city || ''}
                </p>
                <p className="text-sm text-[#626262]">
                  📅 {formatDate(selectedApp.fromDate)} – {formatDate(selectedApp.toDate)}
                </p>
                {selectedApp.tenantFirstName && (
                  <p className="text-sm text-[#626262]">
                    👤 Hyresgäst: {selectedApp.tenantFirstName} {selectedApp.tenantLastName}
                  </p>
                )}
              </div>

              {/* Credit Check Results */}
              {selectedApp.creditCheckAnswers && (
                <div className="border-t border-[#E2EAF0] pt-4 mt-4">
                  <h3 className="text-base font-bold text-[#222222] mb-3 font-['Poppins']">Kreditkontroll</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                      selectedApp.creditCheckAnswers.betalningsanmarkningar === 'ja'
                        ? 'bg-red-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      Betalningsanm: {selectedApp.creditCheckAnswers.betalningsanmarkningar === 'ja' ? 'JA' : 'NEJ'}
                    </span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                      selectedApp.creditCheckAnswers.skuldsaldo === 'ja'
                        ? 'bg-red-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      Kronofogden: {selectedApp.creditCheckAnswers.skuldsaldo === 'ja' ? 'JA' : 'NEJ'}
                    </span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                      selectedApp.creditCheckAnswers.inkomst === 'ja'
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                    }`}>
                      Inkomst OK: {selectedApp.creditCheckAnswers.inkomst === 'ja' ? 'JA' : 'NEJ'}
                    </span>
                    {selectedApp.creditCheckPdfUrl && (
                      <button
                        onClick={() => handlePdfDownload(selectedApp.creditCheckPdfUrl)}
                        disabled={pdfDownloadLoading}
                        className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#62B8E9] text-white hover:bg-[#5ba3d4] transition-colors disabled:opacity-50"
                      >
                        📄 Visa PDF
                      </button>
                    )}
                  </div>

                  {selectedApp.creditCheckComments && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-semibold text-[#626262] mb-1">Kommentar från BRF:</p>
                      <p className="text-sm text-[#222222]">{selectedApp.creditCheckComments}</p>
                    </div>
                  )}

                  {selectedApp.creditCheckDate && (
                    <p className="text-xs text-[#626262] mt-2">
                      Utförd: {new Date(selectedApp.creditCheckDate).toLocaleString('sv-SE')}
                    </p>
                  )}
                </div>
              )}

              {/* Bilagor (read-only för ägare) */}
              <AttachmentsSection
                applicationId={selectedApp.id}
                attachments={selectedApp.attachments || []}
                canUpload={false}
              />

              {/* Additional info for approved applications */}
              {selectedApp.status === 'FINAL_APPROVED' && (
                <div className="border-t border-[#E2EAF0] pt-4 mt-4">
                  <h3 className="text-base font-bold text-[#222222] mb-3 font-['Poppins']">Hyresuppgifter</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-[#626262] mb-1">Hyresavgift</p>
                      <p className="text-sm text-[#222222]">{selectedApp.rentalFee ? `${selectedApp.rentalFee} kr/mån` : 'Ej angivet'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#626262] mb-1">Deposition</p>
                      <p className="text-sm text-[#222222]">{selectedApp.deposit ? `${selectedApp.deposit} kr` : 'Ej angivet'}</p>
                    </div>
                  </div>

                  {selectedApp.finalApprovedAt && (
                    <p className="text-xs text-[#626262] mt-3 pt-3 border-t border-[#E2EAF0]">
                      Godkänd av BRF: {new Date(selectedApp.finalApprovedAt).toLocaleDateString('sv-SE')}
                    </p>
                  )}
                </div>
              )}

              {/* Förlängning — begäran och föreningens beslut.
                  Ägaren såg tidigare ingenting av det här i plattformen; ett
                  avslag nådde bara mejlen, och kommentaren inte ens dit. */}
              {selectedApp.extensionRequest && (
                <div className="border-t border-[#E2EAF0] pt-4 mt-4">
                  <h3 className="text-base font-bold text-[#222222] mb-3 font-['Poppins']">Förlängning</h3>

                  {selectedApp.extensionRequest.status === 'pending' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-blue-900">
                        Du har begärt förlängning till {formatDate(selectedApp.extensionRequest.newEndDate)}.
                      </p>
                      <p className="text-sm text-blue-800 mt-1">
                        Föreningen har ärendet för beslut. Uthyrningen fortsätter under tiden.
                      </p>
                    </div>
                  )}

                  {selectedApp.extensionRequest.status === 'approved' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-green-900">
                        Föreningen godkände förlängningen. Uthyrningen gäller till {formatDate(selectedApp.extensionRequest.newEndDate)}.
                      </p>
                    </div>
                  )}

                  {selectedApp.extensionRequest.status === 'rejected' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-red-900">
                        Föreningen avslog förlängningen till {formatDate(selectedApp.extensionRequest.newEndDate)}.
                      </p>
                      {selectedApp.extensionRequest.rejectionReason ? (
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <p className="text-xs font-semibold text-red-900 mb-1">Föreningens kommentar</p>
                          <p className="text-sm text-red-800 whitespace-pre-line">
                            {selectedApp.extensionRequest.rejectionReason}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-red-800 mt-1">
                          Föreningen lämnade ingen kommentar.
                        </p>
                      )}
                      <p className="text-sm text-red-800 mt-3">
                        Uthyrningen gäller fortfarande till {formatDate(selectedApp.toDate)}. Du kan begära en ny förlängning.
                      </p>
                    </div>
                  )}

                  <div className="text-xs text-[#626262] mt-3 space-y-1">
                    {formatMoment(selectedApp.extensionRequest.requestedDate) && (
                      <p>Begärd {formatMoment(selectedApp.extensionRequest.requestedDate)}</p>
                    )}
                    {selectedApp.extensionRequest.status !== 'pending' &&
                      formatMoment(selectedApp.extensionRequest.processedDate) && (
                        <p>
                          {selectedApp.extensionRequest.status === 'approved' ? 'Godkänd' : 'Avslagen'}{' '}
                          {formatMoment(selectedApp.extensionRequest.processedDate)}
                        </p>
                      )}
                  </div>
                </div>
              )}

              {/* Status notification for CREDIT_CHECK_COMPLETE */}
              {selectedApp.status === 'CREDIT_CHECK_COMPLETE' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-sm font-medium" style={{ color: '#222222' }}>
                    ℹ️ Din BRF granskar nu kreditkontrollen. Du kommer att kunna ta beslut när granskningen är klar.
                  </p>
                </div>
              )}

              {/* Status notification for FINAL_APPROVED - Owner needs to make decision */}
              {selectedApp.status === 'FINAL_APPROVED' && needsOwnerDecision(selectedApp) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-green-800">
                      ✅ BRF har godkänt hyresgästen! Nu är det dags för dig att ta beslut.
                    </p>
                    <button
                      onClick={() => openOwnerDecisionModal(selectedApp)}
                      className="text-white px-4 py-2 rounded-lg text-sm font-semibold ml-4"
                      style={{ backgroundColor: '#22c55e' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#16a34a'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#22c55e'}
                    >
                      Ta beslut
                    </button>
                  </div>
                </div>
              )}

              {/* Rental Management Actions - Endast för aktiva uthyrningar (FINAL_APPROVED) */}
              {selectedApp.status === 'FINAL_APPROVED' && !needsOwnerDecision(selectedApp) && (
                <div className="border-t border-[#E2EAF0] pt-4 mt-4">
                  <h3 className="text-base font-bold text-[#222222] mb-3 font-['Poppins']">Hantera uthyrning</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      onClick={() => {
                        setActiveRental(selectedApp);
                        setShowExtendModal(true);
                        setModalOpen(false);
                      }}
                      className="text-white px-4 py-3 rounded-lg transition-colors text-sm font-semibold flex items-center justify-center"
                      style={{ backgroundColor: '#62B8E9' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9FD1'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                    >
                      Förläng uthyrning
                    </button>
                    <button
                      onClick={() => {
                        setActiveRental(selectedApp);
                        setShowChangeTenantModal(true);
                        setModalOpen(false);
                      }}
                      className="text-white px-4 py-3 rounded-lg transition-colors text-sm font-semibold flex items-center justify-center"
                      style={{ backgroundColor: '#62B8E9' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9FD1'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                    >
                      Byt hyresgäst
                    </button>
                    <button
                      onClick={() => {
                        setActiveRental(selectedApp);
                        setShowTerminateModal(true);
                        setModalOpen(false);
                      }}
                      className="text-white px-4 py-3 rounded-lg transition-colors text-sm font-semibold flex items-center justify-center"
                      style={{ backgroundColor: '#DC2626' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#B91C1C'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#DC2626'}
                    >
                      Avsluta uthyrning
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}
            </>
          )}
        </div>
      </Dialog>

      {/* No Association Modal */}
      <NoAssociationModal
        isOpen={showNoAssociationModal}
        hasPendingClaim={noAssociationPendingClaim}
        onClose={() => setShowNoAssociationModal(false)}
      />

      {/* Owner Decision Modal */}
      {decisionApp && (
        <OwnerDecisionModal
          open={showOwnerDecision}
          onClose={() => {
            setShowOwnerDecision(false);
            setDecisionApp(null);
          }}
          application={decisionApp}
          onDecisionMade={handleOwnerDecision}
        />
      )}

      {/* Rental Management Modals */}
      {activeRental && (
        <>
          <ExtendRentalModal
            open={showExtendModal}
            onClose={() => {
              setShowExtendModal(false);
              setActiveRental(null);
            }}
            currentEndDate={activeRental.toDate}
            apartmentNumber={activeRental.apartmentNumber}
            address={activeRental.address}
            onConfirm={handleExtendRental}
          />

          <ChangeTenantModal
            open={showChangeTenantModal}
            onClose={() => {
              setShowChangeTenantModal(false);
              setActiveRental(null);
            }}
            currentStartDate={activeRental.fromDate}
            currentEndDate={activeRental.toDate}
            currentTenantName={`${activeRental.tenantFirstName} ${activeRental.tenantLastName}`}
            apartmentNumber={activeRental.apartmentNumber}
            address={activeRental.address}
            onConfirm={handleChangeTenant}
          />

          <TerminateRentalModal
            open={showTerminateModal}
            onClose={() => {
              setShowTerminateModal(false);
              setActiveRental(null);
            }}
            tenantName={`${activeRental.tenantFirstName} ${activeRental.tenantLastName}`}
            apartmentNumber={activeRental.apartmentNumber}
            address={activeRental.address}
            contractEndDate={activeRental.toDate}
            onConfirm={handleTerminateRental}
          />
        </>
      )}
    </div>
  );
} 